import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contatti() {
    const [form, setForm] = useState({
        nome: "",
        email: "",
        telefono: "",
        messaggio: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) =>
        setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.nome || !form.email || !form.telefono || !form.messaggio) {
            toast.error("Compila tutti i campi prima di inviare.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success(
                "Messaggio inviato. Ti risponderemo entro 24 ore lavorative.",
            );
            setForm({ nome: "", email: "", telefono: "", messaggio: "" });
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Errore durante l'invio. Riprova fra qualche minuto.";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contatti"
            data-testid="section-contatti"
            className="relative bg-ink-900 py-28 lg:py-36"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <Reveal>
                    <div className="mb-20 max-w-2xl">
                        <p className="text-xs uppercase tracking-widest text-brass">
                            Contatti — 05
                        </p>
                        <h2
                            data-testid="contatti-title"
                            className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                        >
                            Parliamo del tuo
                            <span className="italic text-brass"> progetto</span>
                            .
                        </h2>
                        <p className="mt-6 text-base lg:text-lg text-cream/65 leading-relaxed">
                            Raccontaci la tua idea: ti ricontatteremo per un
                            sopralluogo gratuito e un preventivo dettagliato.
                        </p>
                    </div>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Form */}
                    <Reveal className="lg:col-span-7">
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="space-y-2"
                            noValidate
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
                                <div className="py-2">
                                    <label
                                        htmlFor="nome"
                                        className="block text-[10px] uppercase tracking-widest text-brass mb-1"
                                    >
                                        Nome
                                    </label>
                                    <input
                                        id="nome"
                                        name="nome"
                                        type="text"
                                        value={form.nome}
                                        onChange={onChange}
                                        placeholder="Mario Rossi"
                                        data-testid="contact-input-nome"
                                        className="input-minimal"
                                        autoComplete="name"
                                    />
                                </div>
                                <div className="py-2">
                                    <label
                                        htmlFor="email"
                                        className="block text-[10px] uppercase tracking-widest text-brass mb-1"
                                    >
                                        Email
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        placeholder="mario@email.it"
                                        data-testid="contact-input-email"
                                        className="input-minimal"
                                        autoComplete="email"
                                    />
                                </div>
                                <div className="py-2 md:col-span-2">
                                    <label
                                        htmlFor="telefono"
                                        className="block text-[10px] uppercase tracking-widest text-brass mb-1"
                                    >
                                        Telefono
                                    </label>
                                    <input
                                        id="telefono"
                                        name="telefono"
                                        type="tel"
                                        value={form.telefono}
                                        onChange={onChange}
                                        placeholder="+39 ___ ___ ____"
                                        data-testid="contact-input-telefono"
                                        className="input-minimal"
                                        autoComplete="tel"
                                    />
                                </div>
                                <div className="py-2 md:col-span-2">
                                    <label
                                        htmlFor="messaggio"
                                        className="block text-[10px] uppercase tracking-widest text-brass mb-1"
                                    >
                                        Messaggio
                                    </label>
                                    <textarea
                                        id="messaggio"
                                        name="messaggio"
                                        rows={4}
                                        value={form.messaggio}
                                        onChange={onChange}
                                        placeholder="Descrivi il tuo progetto, la superficie indicativa e i tempi previsti."
                                        data-testid="contact-input-messaggio"
                                        className="input-minimal resize-none"
                                    />
                                </div>
                            </div>

                            <div className="pt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit-button"
                                    className="group inline-flex items-center justify-center gap-3 bg-brass hover:bg-brass-light disabled:opacity-60 disabled:cursor-not-allowed text-ink-900 px-10 py-4 text-sm tracking-widest uppercase transition-colors duration-500"
                                >
                                    {submitting ? "Invio in corso…" : "Invia"}
                                    <ArrowRight
                                        size={16}
                                        className="transition-transform duration-500 group-hover:translate-x-1"
                                    />
                                </button>
                                <p className="text-xs text-cream/40 max-w-sm">
                                    Inviando il modulo acconsenti al
                                    trattamento dei dati ai sensi del GDPR.
                                </p>
                            </div>
                        </form>
                    </Reveal>

                    {/* Info + Map */}
                    <Reveal className="lg:col-span-5" delay={120}>
                        <div className="space-y-6 mb-8">
                            <div className="flex items-start gap-4">
                                <MapPin
                                    size={18}
                                    className="mt-1 text-brass shrink-0"
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-cream/40 mb-1">
                                        Sede
                                    </p>
                                    <p
                                        className="text-cream/85"
                                        data-testid="contact-address"
                                    >
                                        Via Variante S.S. 115, 45
                                        <br />
                                        Modica (RG)
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Phone
                                    size={18}
                                    className="mt-1 text-brass shrink-0"
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-cream/40 mb-1">
                                        Telefono
                                    </p>
                                    <a
                                        href="tel:+393891983122"
                                        data-testid="contact-phone"
                                        className="text-cream/85 hover:text-brass transition-colors"
                                    >
                                        389 198 3122
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Mail
                                    size={18}
                                    className="mt-1 text-brass shrink-0"
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-cream/40 mb-1">
                                        Email
                                    </p>
                                    <a
                                        href="mailto:info@dogaparquet.it"
                                        data-testid="contact-email"
                                        className="text-cream/85 hover:text-brass transition-colors"
                                    >
                                        info@dogaparquet.it
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Clock
                                    size={18}
                                    className="mt-1 text-brass shrink-0"
                                />
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-cream/40 mb-1">
                                        Orari
                                    </p>
                                    <p className="text-cream/85">
                                        Lun — Ven · 8:30 — 18:30
                                        <br />
                                        Sab · su appuntamento
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div
                            data-testid="contact-map"
                            className="relative aspect-[5/4] overflow-hidden border border-white/10"
                        >
                            <iframe
                                title="Dogaparquet — Modica"
                                src="https://www.google.com/maps?q=Via+Variante+S.S.+115,+45+Modica+RG&output=embed"
                                width="100%"
                                height="100%"
                                style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
